import { ReactNode } from 'react';
import { useAuth, OrgRole, PlatformRole } from '../context/AuthContext';

interface RoleGuardProps {
  /** Org-level roles allowed to render children */
  orgRoles?: OrgRole[];
  /** Platform-level roles allowed to render children */
  platformRoles?: PlatformRole[];
  /** Optional fallback when the user does not have permission */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Conditionally render children based on the authenticated user's role(s).
 *
 * Rules:
 *  - If `platformRoles` matches (e.g. super_admin), children are rendered.
 *  - Otherwise, if `orgRoles` matches the user's role in the current org, children are rendered.
 *  - SuperAdmin always passes when only `orgRoles` is provided (handled by useAuth().hasOrgRole).
 *  - Returns `fallback` (or null) when access is denied.
 *
 * Examples:
 *   <RoleGuard orgRoles={['org_admin', 'maintenance']}> <RelayControl /> </RoleGuard>
 *   <RoleGuard platformRoles={['super_admin']}> <AdminPanel /> </RoleGuard>
 */
export function RoleGuard({ orgRoles, platformRoles, fallback = null, children }: RoleGuardProps) {
  const { user, hasOrgRole } = useAuth();

  if (!user) return <>{fallback}</>;

  // Platform check (independent OR with org check)
  if (platformRoles && user.platformRole && platformRoles.includes(user.platformRole)) {
    return <>{children}</>;
  }

  // Org-level check
  if (orgRoles && orgRoles.length > 0 && hasOrgRole(...orgRoles)) {
    return <>{children}</>;
  }

  // No permission rule matched → deny
  if (!orgRoles && !platformRoles) {
    // No constraint provided: render children (fail-open is intentional, prevents accidental hide)
    return <>{children}</>;
  }

  return <>{fallback}</>;
}
